import { HevcPicture } from './heic-container';

/**
 * A coded HEVC picture (Annex B: start codes between the NAL units, the way the WebCodecs `annexb`
 * format writes it) turned into what a HEIF item needs (2026-10-12): the hvcC configuration
 * built from its VPS, SPS and PPS, ispe and pixi, and the picture's slices with 4-byte lengths.
 */

const VPS = 32, SPS = 33, PPS = 34;

/** the NAL units between the start codes */
export function splitAnnexB( bytes: Uint8Array ): Uint8Array[] {
  const units: Uint8Array[] = [];
  let start = -1;
  let i = 0;
  while ( i + 2 < bytes.length ) {
    if ( bytes[ i ] === 0 && bytes[ i + 1 ] === 0 && bytes[ i + 2 ] === 1 ) {
      if ( start >= 0 ) units.push( trimZeros( bytes.subarray( start, i ) ) );
      i += 3;
      start = i;
    } else {
      i++;
    }
  }
  if ( start >= 0 && start < bytes.length ) units.push( trimZeros( bytes.subarray( start ) ) );
  return units.filter( u => u.length > 1 );
}

/** a 4-byte start code leaves a zero at the end of the unit before it */
function trimZeros( unit: Uint8Array ): Uint8Array {
  let end = unit.length;
  while ( end > 0 && unit[ end - 1 ] === 0 ) end--;
  return unit.subarray( 0, end );
}

function nalType( unit: Uint8Array ): number {
  return ( unit[ 0 ] >> 1 ) & 0x3f;
}

/** the unit without its emulation prevention bytes (00 00 03 -> 00 00) */
function rbsp( unit: Uint8Array ): Uint8Array {
  const out: number[] = [];
  let zeros = 0;
  for ( let i = 0; i < unit.length; i++ ) {
    const b = unit[ i ];
    if ( zeros >= 2 && b === 3 ) {
      zeros = 0;
      continue;
    }
    zeros = b === 0 ? zeros + 1 : 0;
    out.push( b );
  }
  return Uint8Array.from( out );
}

class Bits {
  private pos = 0;

  constructor( private data: Uint8Array ) {
  }

  bit(): number {
    const b = ( this.data[ this.pos >> 3 ] >> ( 7 - ( this.pos & 7 ) ) ) & 1;
    this.pos++;
    return b;
  }

  bits( n: number ): number {
    let v = 0;
    for ( let i = 0; i < n; i++ ) v = v * 2 + this.bit();
    return v;
  }

  skip( n: number ): void {
    this.pos += n;
  }

  se(): number {
    const k = this.ue();
    return k % 2 ? ( k + 1 ) / 2 : -k / 2;
  }

  ue(): number {
    let zeros = 0;
    while ( this.bit() === 0 ) {
      if ( ++zeros > 31 ) throw new Error( 'HEVC: broken exp-Golomb code' );
    }
    return ( Math.pow( 2, zeros ) - 1 ) + this.bits( zeros );
  }
}

interface SpsInfo {
  /** profile_tier_level's general part: 12 bytes, as hvcC carries them */
  general: Uint8Array;
  chromaFormat: number;
  bitDepthLuma: number;
  bitDepthChroma: number;
  /** the colour the stream's VUI declares, when it declares one */
  colour?: { primaries: number; transfer: number; matrix: number; fullRange: boolean };
}

function readSps( unit: Uint8Array ): SpsInfo {
  const data = rbsp( unit );
  const r = new Bits( data );
  r.skip( 16 ); // NAL header
  r.skip( 4 ); // sps_video_parameter_set_id
  const subLayers = r.bits( 3 );
  r.skip( 1 ); // temporal id nesting
  // general_profile_space .. general_level_idc: 2 + 1 + 5 + 32 + 48 + 8 bits, byte aligned here
  const general = data.slice( 3, 15 );
  r.skip( 96 );
  // sub layers (encoders write none for a still picture, but read them right)
  const present: [ number, number ][] = [];
  for ( let i = 0; i < subLayers; i++ ) present.push( [ r.bit(), r.bit() ] );
  if ( subLayers > 0 ) for ( let i = subLayers; i < 8; i++ ) r.skip( 2 );
  for ( const [ profile, level ] of present ) {
    if ( profile ) r.skip( 88 );
    if ( level ) r.skip( 8 );
  }
  r.ue(); // sps_seq_parameter_set_id
  const chromaFormat = r.ue();
  if ( chromaFormat === 3 ) r.skip( 1 );
  r.ue(); // pic_width_in_luma_samples
  r.ue(); // pic_height_in_luma_samples
  if ( r.bit() ) {
    r.ue(); r.ue(); r.ue(); r.ue(); // conformance window
  }
  const bitDepthLuma = r.ue() + 8;
  const bitDepthChroma = r.ue() + 8;
  const info: SpsInfo = { general, chromaFormat, bitDepthLuma, bitDepthChroma };
  try {
    info.colour = readVuiColour( r, subLayers );
  } catch {
    // a stream this reader does not follow to its VUI keeps the colour the page converted with
  }
  return info;
}

/**
 * The rest of the SPS up to the VUI's colour description: the encoder says there how it stored the
 * picture (an encoder may change the range it is given), and the HEIC's colr has to say the same.
 */
function readVuiColour( r: Bits, subLayers: number ): SpsInfo[ 'colour' ] {
  r.ue(); // log2_max_pic_order_cnt_lsb_minus4
  const ordering = r.bit();
  for ( let i = ordering ? 0 : subLayers; i <= subLayers; i++ ) {
    r.ue(); r.ue(); r.ue();
  }
  for ( let i = 0; i < 6; i++ ) r.ue(); // coding and transform block sizes, hierarchy depths
  if ( r.bit() && r.bit() ) {
    // scaling_list_data
    for ( let sizeId = 0; sizeId < 4; sizeId++ ) {
      for ( let matrixId = 0; matrixId < 6; matrixId += sizeId === 3 ? 3 : 1 ) {
        if ( !r.bit() ) {
          r.ue();
        } else {
          const coefs = Math.min( 64, 1 << ( 4 + ( sizeId << 1 ) ) );
          if ( sizeId > 1 ) r.se();
          for ( let k = 0; k < coefs; k++ ) r.se();
        }
      }
    }
  }
  r.skip( 2 ); // amp, sample adaptive offset
  if ( r.bit() ) {
    // pcm
    r.skip( 8 );
    r.ue(); r.ue();
    r.skip( 1 );
  }
  const sets = r.ue();
  const deltaPocs: number[] = [];
  for ( let idx = 0; idx < sets; idx++ ) {
    if ( idx > 0 && r.bit() ) {
      // predicted from the set before it
      r.skip( 1 );
      r.ue();
      let count = 0;
      for ( let j = 0; j <= deltaPocs[ idx - 1 ]; j++ ) {
        const used = r.bit();
        if ( used || r.bit() ) count++;
      }
      deltaPocs.push( count );
    } else {
      const negative = r.ue(), positive = r.ue();
      for ( let k = 0; k < negative + positive; k++ ) {
        r.ue();
        r.skip( 1 );
      }
      deltaPocs.push( negative + positive );
    }
  }
  if ( r.bit() ) {
    throw new Error( 'long term reference pictures: not followed' );
  }
  r.skip( 2 ); // temporal mvp, strong intra smoothing
  if ( !r.bit() ) return undefined; // no VUI
  if ( r.bit() && r.bits( 8 ) === 255 ) r.skip( 32 ); // aspect ratio
  if ( r.bit() ) r.skip( 1 ); // overscan
  if ( !r.bit() ) return undefined; // no video signal type
  r.skip( 3 ); // video_format
  const fullRange = r.bit() === 1;
  if ( !r.bit() ) return undefined; // no colour description
  return { primaries: r.bits( 8 ), transfer: r.bits( 8 ), matrix: r.bits( 8 ), fullRange };
}

function box( type: string, body: number[] ): Uint8Array {
  const size = body.length + 8;
  return Uint8Array.from( [ size >>> 24, ( size >>> 16 ) & 255, ( size >>> 8 ) & 255, size & 255,
    ...type.split( '' ).map( c => c.charCodeAt( 0 ) ), ...body ] );
}

const u32 = ( v: number ) => [ v >>> 24, ( v >>> 16 ) & 255, ( v >>> 8 ) & 255, v & 255 ];
const u16 = ( v: number ) => [ ( v >>> 8 ) & 255, v & 255 ];

/** the HEVCDecoderConfigurationRecord: the stream's parameter sets, 4-byte NAL lengths */
function hvcC( vps: Uint8Array[], sps: Uint8Array[], pps: Uint8Array[], info: SpsInfo ): Uint8Array {
  const body: number[] = [ 1, ...Array.from( info.general ) ];
  body.push( 0xf0, 0x00 ); // min_spatial_segmentation_idc 0
  body.push( 0xfc ); // parallelismType 0
  body.push( 0xfc | info.chromaFormat );
  body.push( 0xf8 | ( info.bitDepthLuma - 8 ) );
  body.push( 0xf8 | ( info.bitDepthChroma - 8 ) );
  body.push( 0, 0 ); // avgFrameRate
  // constantFrameRate 0, numTemporalLayers 1, temporalIdNested 1, lengthSizeMinusOne 3
  body.push( ( 1 << 3 ) | ( 1 << 2 ) | 3 );
  const arrays = [ [ VPS, vps ], [ SPS, sps ], [ PPS, pps ] ] as [ number, Uint8Array[] ][];
  body.push( arrays.length );
  for ( const [ type, units ] of arrays ) {
    body.push( 0x80 | type, ...u16( units.length ) );
    for ( const unit of units ) body.push( ...u16( unit.length ), ...Array.from( unit ) );
  }
  return box( 'hvcC', body );
}

/** one coded picture (Annex B) as a HEIF item of `width` × `height` */
export function pictureFromAnnexB( bytes: Uint8Array, width: number, height: number ): HevcPicture {
  return pictureFromUnits( splitAnnexB( bytes ), [], width, height );
}

/** whether the bytes start with an Annex B start code (00 00 01 or 00 00 00 01) */
export function isAnnexB( bytes: Uint8Array ): boolean {
  return bytes.length > 4 && bytes[ 0 ] === 0 && bytes[ 1 ] === 0 && ( bytes[ 2 ] === 1 || ( bytes[ 2 ] === 0 && bytes[ 3 ] === 1 ) );
}

/** the NAL units of an HEVCDecoderConfigurationRecord (the hvcC body), and its NAL length size */
export function readHvcCRecord( record: Uint8Array ): { units: Uint8Array[]; lengthSize: number } {
  if ( record.length < 23 ) throw new Error( 'HEVC: the decoder configuration is too short' );
  const lengthSize = ( record[ 21 ] & 3 ) + 1;
  const units: Uint8Array[] = [];
  let o = 23;
  for ( let a = 0; a < record[ 22 ]; a++ ) {
    const count = ( record[ o + 1 ] << 8 ) | record[ o + 2 ];
    o += 3;
    for ( let n = 0; n < count; n++ ) {
      const len = ( record[ o ] << 8 ) | record[ o + 1 ];
      units.push( record.subarray( o + 2, o + 2 + len ) );
      o += 2 + len;
    }
  }
  return { units, lengthSize };
}

/** the NAL units of a stream with `lengthSize`-byte lengths in front of each (the "hvc" format) */
export function splitLengthPrefixed( bytes: Uint8Array, lengthSize: number ): Uint8Array[] {
  const units: Uint8Array[] = [];
  let o = 0;
  while ( o + lengthSize <= bytes.length ) {
    let len = 0;
    for ( let i = 0; i < lengthSize; i++ ) len = len * 256 + bytes[ o + i ];
    o += lengthSize;
    if ( len <= 0 || o + len > bytes.length ) throw new Error( 'HEVC: a broken NAL length' );
    units.push( bytes.subarray( o, o + len ) );
    o += len;
  }
  return units;
}

/**
 * One coded picture as an encoder gave it, in either of the two WebCodecs formats: Annex B (start codes,
 * the parameter sets inline) or "hvc" (lengths in front of the units, the parameter sets in the decoder
 * configuration `description`). A browser may hand back the other one than asked for.
 */
export function pictureFromStream( bytes: Uint8Array, description: Uint8Array | null, width: number, height: number ): HevcPicture {
  const record = description && description.length >= 23 ? readHvcCRecord( description ) : null;
  const units = isAnnexB( bytes ) ? splitAnnexB( bytes ) : splitLengthPrefixed( bytes, record ? record.lengthSize : 4 );
  return pictureFromUnits( units, record ? record.units : [], width, height );
}

/** the picture's units (and the configuration's, for parameter sets the stream does not carry) as an item */
function pictureFromUnits( units: Uint8Array[], configUnits: Uint8Array[], width: number, height: number ): HevcPicture {
  const of = ( type: number ) => {
    const inline = units.filter( u => nalType( u ) === type );
    return inline.length ? inline : configUnits.filter( u => nalType( u ) === type );
  };
  const vps = of( VPS );
  const sps = of( SPS );
  const pps = of( PPS );
  // the picture itself: its slices (VCL units, types 0..31); access unit delimiters and SEI stay out
  const slices = units.filter( u => nalType( u ) < 32 );
  if ( !vps.length || !sps.length || !pps.length || !slices.length ) {
    throw new Error( 'HEVC: the stream has no parameter sets or no picture' );
  }
  const info = readSps( sps[ 0 ] );
  const length = slices.reduce( ( s, u ) => s + 4 + u.length, 0 );
  const data = new Uint8Array( length );
  let at = 0;
  for ( const u of slices ) {
    data.set( u32( u.length ), at );
    data.set( u, at + 4 );
    at += 4 + u.length;
  }
  const properties = [
    hvcC( vps, sps, pps, info ),
    box( 'ispe', [ 0, 0, 0, 0, ...u32( width ), ...u32( height ) ] ),
    box( 'pixi', [ 0, 0, 0, 0, 3, info.bitDepthLuma, info.bitDepthChroma, info.bitDepthChroma ] ),
  ];
  // the colour as the stream stored it; without one the container writes the page's own (sRGB, BT.601 full)
  const c = info.colour;
  if ( c ) {
    properties.push( box( 'colr', [ ...'nclx'.split( '' ).map( ch => ch.charCodeAt( 0 ) ), ...u16( c.primaries ), ...u16( c.transfer ), ...u16( c.matrix ), c.fullRange ? 0x80 : 0 ] ) );
  }
  return { width, height, data, properties };
}

/** RGBA to planar 4:2:0 (I420), BT.601 full range: the conversion the HEIC's nclx colr declares */
export function rgbaToI420( rgba: Uint8ClampedArray | Uint8Array, width: number, height: number ): Uint8Array {
  const cw = width >> 1, ch = height >> 1;
  const out = new Uint8Array( width * height + 2 * cw * ch );
  const clamp = ( v: number ) => v < 0 ? 0 : v > 255 ? 255 : v;
  for ( let i = 0, p = 0; i < width * height; i++, p += 4 ) {
    out[ i ] = clamp( Math.floor( 0.299 * rgba[ p ] + 0.587 * rgba[ p + 1 ] + 0.114 * rgba[ p + 2 ] + 0.5 ) );
  }
  const uAt = width * height, vAt = uAt + cw * ch;
  for ( let y = 0; y < ch; y++ ) {
    for ( let x = 0; x < cw; x++ ) {
      let r = 0, g = 0, b = 0;
      for ( let dy = 0; dy < 2; dy++ ) {
        for ( let dx = 0; dx < 2; dx++ ) {
          const p = ( ( y * 2 + dy ) * width + x * 2 + dx ) * 4;
          r += rgba[ p ]; g += rgba[ p + 1 ]; b += rgba[ p + 2 ];
        }
      }
      r /= 4; g /= 4; b /= 4;
      out[ uAt + y * cw + x ] = clamp( Math.floor( -0.168736 * r - 0.331264 * g + 0.5 * b + 128.5 ) );
      out[ vAt + y * cw + x ] = clamp( Math.floor( 0.5 * r - 0.418688 * g - 0.081312 * b + 128.5 ) );
    }
  }
  return out;
}
